import React, { useEffect, useRef, useState } from 'react';
import { UploadFileToS3 } from './UploadFileToS3';
import { useAuthContext } from '../context/AuthContext';
import { useQuery, useMutation } from '@apollo/client';
import { GET_AVATAR_USER_MANAGE } from '../graphql-client/Queries';
import { PUBLISH_USER } from '../graphql-client/Mutation';
import QuizExcelFormatPreview from './QuizExcelFormatPreview';
import { parseQuizExcelFromArrayBuffer } from '../utils/quizExcelParser';
import { parseQuizDocxFromArrayBuffer } from '../utils/quizDocxParser';

export default function ComponentUploadFileToS3({ formState, setFormState, isUpdate, onUploadStatusChange }) {
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [process, setProcess] = useState(0);
    const [quizFormatResult, setQuizFormatResult] = useState(null);
    const [isCheckingQuizFormat, setIsCheckingQuizFormat] = useState(false);
    const { userId } = useAuthContext();
    const { data } = useQuery(GET_AVATAR_USER_MANAGE);
    const [updateMonthlyUploadSize] = useMutation(PUBLISH_USER);
    const uploadStatusChangeRef = useRef(onUploadStatusChange);

    useEffect(() => {
        uploadStatusChangeRef.current = onUploadStatusChange;
    }, [onUploadStatusChange]);

    useEffect(() => {
        uploadStatusChangeRef.current?.(isLoading);
    }, [isLoading]);

    // Danh sách file types được phép
    const ALLOWED_FILE_TYPES = {
        pdf: {
            mimeTypes: ['application/pdf'],
            extensions: ['.pdf'],
            label: 'PDF',
            icon: 'fas fa-file-pdf text-danger',
            description: 'Tài liệu học tập, giáo trình'
        },
        excel: {
            mimeTypes: [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ],
            extensions: ['.xls', '.xlsx', '.csv'],
            label: 'Excel',
            icon: 'fas fa-file-excel text-success',
            description: 'File Excel chuẩn hóa sẽ hiển thị theo đúng định dạng bài tập Quiz (Câu n, *A.)',
            templateUrl: 'https://data.linklearn.vn/template_quiz.xlsx', // Template file
            templateName: 'template_quiz.xlsx'
        },
        docx: {
            mimeTypes: [
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/msword'
            ],
            extensions: ['.docx'],
            label: 'Word',
            icon: 'fas fa-file-word text-primary',
            description: 'File Word chuẩn hóa sẽ hiển thị theo đúng định dạng bài tập Quiz (Câu n, *A.)',
            templateUrl: 'https://data.linklearn.vn/%C4%90%E1%BB%81%20m%E1%BA%ABu_v4.docx',
            templateName: 'template_quiz_word.docx',
            guideUrl: 'https://data.linklearn.vn/H%C6%AF%E1%BB%9ANG%20D%E1%BA%AAN%20CHU%E1%BA%A8N%20H%C3%93A%20FILE%20WORD%20CHO%20LINKLEARN.pdf',
            guideName: 'huong-dan-chuan-hoa-quiz-docx.pdf'
        }
    };

    const isValidFileType = (file) => {
        if (!file) return false;
        const fileName = file.name.toLowerCase();
        const fileType = file.type;

        // Kiểm tra qua MIME type hoặc extension
        for (const category of Object.values(ALLOWED_FILE_TYPES)) {
            // Kiểm tra MIME type
            if (category.mimeTypes.includes(fileType)) {
                return true;
            }
            
            // Kiểm tra extension (fallback nếu MIME type không chính xác)
            if (category.extensions.some(ext => fileName.endsWith(ext))) {
                return true;
            }
        }
        return false;
    };

    const getFileType = (file) => {
        if (!file) return 'unknown';
        const fileName = file.name.toLowerCase();
        const fileType = file.type;

        if (ALLOWED_FILE_TYPES.pdf.mimeTypes.includes(fileType) || 
            ALLOWED_FILE_TYPES.pdf.extensions.some(ext => fileName.endsWith(ext))) {
            return 'pdf';
        }

        if (ALLOWED_FILE_TYPES.excel.mimeTypes.includes(fileType) || 
            ALLOWED_FILE_TYPES.excel.extensions.some(ext => fileName.endsWith(ext))) {
            return 'excel';
        }

        if (ALLOWED_FILE_TYPES.docx.mimeTypes.includes(fileType) || 
            ALLOWED_FILE_TYPES.docx.extensions.some(ext => fileName.endsWith(ext))) {
            return 'docx';
        }

        return 'unknown';
    };

    const handleUploadFile = async () => {
        if (!file) {
            alert('Vui lòng chọn một file trước khi tải lên.');
            return;
        }

        if (['excel', 'docx'].includes(getFileType(file)) && quizFormatResult?.errors?.length > 0) {
            alert('File Quiz chưa đúng format. Vui lòng sửa lỗi trước khi tải lên.');
            return;
        }

        if (isLoading) {
            alert('Đang tải lên, vui lòng chờ...');
            return;
        }

        if (formState.urlDocument) {
            alert('Đã có file được tải lên rồi.');
            return;
        }

        if (!data?.userManage) {
            alert('Không thể lấy thông tin người dùng.');
            return;
        }

        try {
            const fileSize = file.size / (1024 * 1024);
            console.log('Dung lượng file:', fileSize.toFixed(2), 'MB');
            console.log('Loại file:', getFileType(file));

            if (data.userManage.monthlyUploadSize > 0 && fileSize < data.userManage.monthlyUploadSize && fileSize < 250) {
                await UploadFileToS3(file, setFile, formState, setFormState, setIsLoading, setProcess, userId);
                await handleUpdateMonthlyUploadSize(fileSize);
            } else {
                alert('Dung lượng tải lên trong tháng không đủ hoặc file nặng hơn 250MB.');
            }
        } catch (error) {
            console.error('Lỗi khi tải lên file:', error);
            alert('Có lỗi xảy ra khi tải lên file. Vui lòng thử lại.');
            setIsLoading(false);
        }
    };

    /**
     * Hàm kiểm tra định dạng tệp Quiz
     * Chuyển đổi tệp Excel hoặc DOCX thành định dạng JSON để phân tích cấu trúc
     * @param {File} selectedFile - Tệp được chọn để kiểm tra
     * @returns {void}
     */
    const validateQuizFormat = async (selectedFile) => {
        setIsCheckingQuizFormat(true);
        try {
            const selectedType = getFileType(selectedFile);

            const arrayBuffer = await selectedFile.arrayBuffer();
            let parsedResult = null;

            if (selectedType === 'excel') {
                parsedResult = parseQuizExcelFromArrayBuffer(arrayBuffer);
            } else if (selectedType === 'docx') {
                const docxResult = await parseQuizDocxFromArrayBuffer(arrayBuffer);
                parsedResult = {
                    sheetName: 'DOCX',
                    ...docxResult,
                };
            }

            if (!parsedResult) {
                throw new Error('Định dạng file Quiz chưa được hỗ trợ để kiểm tra.');
            }

            setQuizFormatResult(parsedResult);
        } catch (error) {
            setQuizFormatResult({
                sheetName: 'N/A',
                questions: [],
                warnings: [],
                errors: [error.message || 'Không thể đọc file Excel.'],
                stats: {
                    totalRows: 0,
                    validRows: 0,
                    invalidRows: 0,
                },
            });
        } finally {
            setIsCheckingQuizFormat(false);
        }
    };

    const handleUpdateMonthlyUploadSize = async (fileSize) => {
        try {
            console.log('Đã chạy handleUpdateMonthlyUploadSize');
            await updateMonthlyUploadSize({ 
                variables: { monthlyUploadSize: Math.round(fileSize) } 
            });
        } catch (error) {
            console.error("Lỗi khi cập nhật dung lượng upload:", error);
        }
    };

    const handleFileSelect = async (e) => {
        const selectedFile = e.target.files[0];

        if (!selectedFile) {
            setFile(null);
            setQuizFormatResult(null);
            setIsCheckingQuizFormat(false);
            return;
        }

        if (!isValidFileType(selectedFile)) {
            const allowedTypes = [
                ...ALLOWED_FILE_TYPES.pdf.extensions,
                ...ALLOWED_FILE_TYPES.excel.extensions
            ].join(', ');
            
            alert(`Chỉ hỗ trợ file: ${allowedTypes}`);
            e.target.value = '';
            setFile(null);
            setQuizFormatResult(null);
            setIsCheckingQuizFormat(false);
            return;
        }

        const fileSizeMB = selectedFile.size / (1024 * 1024);
        if (fileSizeMB > 250) {
            alert('File không được vượt quá 250MB.');
            e.target.value = '';
            setFile(null);
            setQuizFormatResult(null);
            setIsCheckingQuizFormat(false);
            return;
        }

        setFile(selectedFile);

        if (['excel', 'docx'].includes(getFileType(selectedFile))) {
            await validateQuizFormat(selectedFile);
        } else {
            setQuizFormatResult(null);
            setIsCheckingQuizFormat(false);
        }
    };

    const forceDownloadFile = async (fileUrl, fileName) => {
        const response = await fetch(fileUrl);
        if (!response.ok) {
            throw new Error(`Download failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
    };

    /**
     * Download file mẫu theo từng loại Quiz
     */
    const handleDownloadTemplate = (fileCategory) => {
        const templateUrl = ALLOWED_FILE_TYPES[fileCategory]?.templateUrl;
        const templateName = ALLOWED_FILE_TYPES[fileCategory]?.templateName;

        if (!templateUrl || !templateName) {
            alert('Chưa cấu hình file mẫu cho định dạng này.');
            return;
        }

        const link = document.createElement('a');
        link.href = templateUrl;
        link.download = templateName;
        link.click();
    };

    const handleDownloadGuide = async (fileCategory) => {
        const guideUrl = ALLOWED_FILE_TYPES[fileCategory]?.guideUrl;
        const guideName = ALLOWED_FILE_TYPES[fileCategory]?.guideName;

        if (!guideUrl || !guideName) {
            alert('Chưa cấu hình file hướng dẫn cho định dạng này.');
            return;
        }

        try {
            await forceDownloadFile(guideUrl, guideName);
        } catch (error) {
            console.error('Lỗi tải file hướng dẫn:', error);
            alert('Không thể tải file hướng dẫn tự động. Vui lòng thử lại sau.');
        }
    };

    const currentFileType = file ? getFileType(file) : null;
    const fileIcon = currentFileType && ALLOWED_FILE_TYPES[currentFileType] 
        ? ALLOWED_FILE_TYPES[currentFileType].icon 
        : 'fas fa-file';
    const hasQuizFormatErrors = ['excel', 'docx'].includes(currentFileType) && quizFormatResult?.errors?.length > 0;

    return (
        <div className='p-2'>
            {formState.urlDocument ? (
                <div className="alert alert-success d-flex align-items-center" role="alert">
                    <i className="fas fa-check-circle me-2"></i>
                    {isUpdate ? (
                        <p className="mb-0">
                            Đã tải lên tài liệu, nhấn <strong>Cập nhật</strong> để xác nhận cập nhật tài liệu
                        </p>
                    ) : (
                        <p className="mb-0">
                            Đã tải lên file tài liệu, hãy tiếp tục hoàn thiện chi tiết Tài liệu của bạn
                        </p>
                    )}
                </div>
            ) : (
                <>
                    {/* File Type Info với Download Template */}
                    <div className="alert alert-info border mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                                <small>
                                    <i className="fas fa-info-circle me-2"></i>
                                    <strong>File được hỗ trợ:</strong>
                                </small>
                                <ul className="mb-0 mt-1 small">
                                    <li className="mb-1">
                                        <i className={ALLOWED_FILE_TYPES.pdf.icon + ' me-1'}></i>
                                        <strong>PDF</strong> ({ALLOWED_FILE_TYPES.pdf.extensions.join(', ')})
                                        <br />
                                        <small className="text-muted ms-3">
                                            → {ALLOWED_FILE_TYPES.pdf.description}
                                        </small>
                                    </li>
                                     <li className="mt-1">
                                        <i className={ALLOWED_FILE_TYPES.docx.icon + ' me-1'}></i>
                                        <strong>Word</strong> ({ALLOWED_FILE_TYPES.docx.extensions.join(', ')})
                                        <br />
                                        <small className="text-muted ms-3">
                                            → {ALLOWED_FILE_TYPES.docx.description}
                                        </small>
                                        <br />
                                        <small className="ms-3">
                                            <a
                                                href={ALLOWED_FILE_TYPES.docx.guideUrl}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleDownloadGuide('docx');
                                                }}
                                                className="text-primary text-decoration-underline"
                                            >
                                                Xem hướng dẫn chuẩn hóa file word
                                            </a>
                                        </small>
                                    </li>
                                    <li>
                                        <i className={ALLOWED_FILE_TYPES.excel.icon + ' me-1'}></i>
                                        <strong>Excel</strong> ({ALLOWED_FILE_TYPES.excel.extensions.join(', ')})
                                        <br />
                                        <small className="text-muted ms-3">
                                            → {ALLOWED_FILE_TYPES.excel.description}
                                        </small>
                                    </li>
                                   
                                </ul>
                                <small className="d-block mt-2">
                                    <i className="fas fa-weight-hanging me-1"></i>
                                    <strong>Kích thước tối đa:</strong> 250MB
                                </small>
                            </div>
                        </div>

                        {/* Download Template Buttons */}
                        <div className="mt-2 pt-2 border-top">
                            <div className="d-flex flex-wrap gap-2">
                                <button 
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleDownloadTemplate('excel')}
                                    title="Tải xuống file mẫu Excel để tạo Quiz"
                                >
                                    <i className="fas fa-download me-1"></i>
                                    Tải file mẫu Excel Quiz
                                </button>
                                <button 
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleDownloadTemplate('docx')}
                                    title="Tải xuống file mẫu Word để tạo Quiz"
                                >
                                    <i className="fas fa-download me-1"></i>
                                    Tải file mẫu Word Quiz
                                </button>
                            </div>
                            <small className="d-block text-muted mt-1">
                                <i className="fas fa-lightbulb me-1"></i>
                                Nếu upload file Excel Quiz, các ký hiệu toán học ghi bằng latex trong excel, vui lòng sử dụng đúng định dạng mẫu
                            </small>
                        </div>
                    </div>
                    {/* File Input */}
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        <div className="flex-grow-1">
                            <input
                                type="file"
                                title='Tải lên tài liệu'
                                accept=".pdf,.xls,.xlsx,.csv,.docx"
                                onClick={(e) => {
                                    e.target.value = '';
                                }}
                                onChange={handleFileSelect}
                                className="form-control"
                            />
                            
                            {/* Selected File Info */}
                            {file && (
                                <>
                                    <small className="text-muted d-block mt-1">
                                        <i className={fileIcon + ' me-1'}></i>
                                        <strong>{file.name}</strong> 
                                        ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                                        {['excel', 'docx'].includes(currentFileType) && (
                                            <span className="badge bg-warning text-dark ms-2">
                                                <i className="fas fa-exclamation-triangle me-1"></i>
                                                Đảm bảo file đúng định dạng mẫu
                                            </span>
                                        )}
                                    </small>

                                    {['excel', 'docx'].includes(currentFileType) && (
                                        <QuizExcelFormatPreview
                                            result={quizFormatResult}
                                            isChecking={isCheckingQuizFormat}
                                            previewMaxHeight={550}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        {/* Upload Button */}
                        {file && !formState.urlDocument && (
                            <div className='ps-3'>
                                <button
                                    onClick={handleUploadFile}
                                    disabled={isLoading || !!formState.urlDocument || !file || hasQuizFormatErrors || isCheckingQuizFormat}
                                    className='btn btn-info'
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                            Đang tải...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-cloud-upload-alt me-1"></i>
                                            Tải lên
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Progress Bar */}
            {isLoading && (
                <div className="mt-3">
                    <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Đang tải lên...</small>
                        <small className="text-muted">{process}%</small>
                    </div>
                    <div className="progress">
                        <div
                            className="progress-bar progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            style={{ width: process + '%' }}
                            aria-valuenow={process}
                            aria-valuemin="0"
                            aria-valuemax="100"
                        >
                            {process}%
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
